import React, {useMemo} from "react";
import {Link, useTranslation} from "gatsby-plugin-react-i18next";
import {ArrowRight, Binoculars, BookOpen, Broadcast, Handshake, Lightning, ListNumbers} from "@phosphor-icons/react";
import {Button} from "../../components/ui/button";
import SectionHeading from "./section-heading";
import WidgetDemoFrame from "./widget-demo-frame";
import {pizzeria} from "./demo-scenarios";
import team from "../../images/home/pizzeria-team.webp";

const Tile = ({icon: Icon, name, rest, detail}) => (
    <div className="bg-[var(--cl-surface)] p-6 sm:p-7 flex flex-col gap-2.5">
        <Icon size={22} weight="regular" color="var(--cl-ink)"/>
        <h3 className="text-[17px] leading-snug">
            <span className="font-bold text-[var(--cl-ink)]">{name}</span>
            {" "}
            <span className="font-normal text-[var(--cl-muted)]">{rest}</span>
        </h3>
        <p className="text-[17px] leading-relaxed text-[var(--cl-ink-2)]">{detail}</p>
    </div>
);

const PowerfulFeatures = () => {
    const {t, i18n} = useTranslation();
    const scenario = useMemo(() => pizzeria(t), [t, i18n.language]);

    const left = [
        {
            icon: BookOpen,
            name: t("Knowledge"),
            rest: t("from your website, documents and FAQs, refreshed automatically."),
            detail: t("Add a link, a PDF or a spreadsheet. The agent re-reads your site on a schedule, so it never quotes last season's prices."),
        },
        {
            icon: Lightning,
            name: t("Actions"),
            rest: t("in your own systems, in the middle of a conversation."),
            detail: t("Order status, stock, room availability, a booking. Ready-made integrations or your own API."),
        },
        {
            icon: ListNumbers,
            name: t("Guided conversations (Flow Editor)"),
            rest: t("you draw the stages of a booking, a qualification or a complaint, and the agent stays inside them."),
            detail: t("Plain-language conditions between stages. No improvising where you do not want it."),
        },
    ];
    const right = [
        {
            icon: Broadcast,
            name: t("Three channels plus e-mail"),
            rest: t("chat, phone and WhatsApp answered by the agent. E-mail with replies drafted for your team."),
            detail: t("One body of knowledge. The customer picks how to reach you and gets the same answer."),
        },
        {
            icon: Handshake,
            name: t("Handover to a human"),
            rest: t("with the whole conversation attached."),
            detail: t("Nobody starts from scratch and nobody asks the customer to repeat themselves."),
        },
        {
            icon: Binoculars,
            name: t("Insight"),
            rest: t("into what customers ask and where the agent had no answer."),
            detail: t("AI Insights show the gaps. Corrections from the chat log teach the agent the right reply."),
        },
    ];

    return (
        <div className="w-full max-w-screen-2xl mx-auto px-5">
            <section className="w-full bg-[var(--cl-surface)] border border-[var(--cl-rule)]">
                <SectionHeading
                    className="pt-10 sm:pt-14 pb-8 px-6 sm:px-10"
                    eyebrow={t("What the agent can do")}
                    title={t("Six things that decide|whether a customer gets helped or gives up.")}
                    lead={t("In the middle: Pizza Delicious taking an evening order, played back in the real widget.")}
                />

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)_minmax(0,1fr)] gap-px bg-[var(--cl-rule)] border-t border-[var(--cl-rule)]">
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-px bg-[var(--cl-rule)]">
                        {left.map((c) => <Tile key={c.name} {...c}/>)}
                    </div>
                    <div className="relative bg-[var(--cl-surface-2)] overflow-hidden lg:min-h-[1000px]">
                        <img
                            src={team}
                            alt={t("Two pizzeria owners in their kitchen")}
                            width={760}
                            height={1520}
                            loading="lazy"
                            className="hidden lg:block absolute inset-0 w-full h-full object-cover object-top"
                        />
                        <div className="p-5 sm:p-6 flex justify-center lg:p-0 lg:absolute lg:inset-x-6 lg:top-[420px] lg:bottom-5">
                            <div className="w-full max-w-[448px]">
                                <WidgetDemoFrame scenario={scenario} height={560} label={false}/>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-px bg-[var(--cl-rule)]">
                        {right.map((c) => <Tile key={c.name} {...c}/>)}
                    </div>
                </div>

                <div className="px-6 sm:px-10 py-7 border-t border-[var(--cl-rule)] flex flex-wrap items-center gap-4">
                    <p className="text-base text-[var(--cl-muted)] m-0">
                        {t("Voice models, own phone number, forms, e-commerce integrations, API, spam filter and more.")}
                    </p>
                    <Link to="/features/" className="ml-auto">
                        <Button variant="regular-outline-black" className="px-5 flex items-center gap-3 whitespace-nowrap text-base">
                            {t("See all features")}
                            <ArrowRight size={16} weight="bold"/>
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default PowerfulFeatures;
